import type {
	Commodity,
	CommodityNewBody,
} from "~/shared/schemas/commodity-schema";
import type { HandlingUnit } from "~/shared/types/quote-details-types";
import { calculateItemDensity } from "~/shared/utils/commodity-utils.ts";

import { imperialMetricConverter } from "./imperial-metric-converter.ts";

export const convertCommodityToHandlingUnit = (
	commodity: Commodity,
	measuringType?: string,
): HandlingUnit => {
	const weight = commodity.weight ?? 0;
	const length = commodity.dimensionLength ?? 0;
	const width = commodity.dimensionWidth ?? 0;
	const height = commodity.dimensionHeight ?? 0;

	const isMetric = measuringType === "KG/CM";

	const converted = isMetric
		? imperialMetricConverter("KG/CM", {
				weight,
				length,
				width,
				height,
			})
		: {
				weight,
				length,
				width,
				height,
			};

	const hu: HandlingUnit = {
		// Map existing fields
		id: commodity.id,
		commodityId: commodity.id,
		description: commodity.description ?? "",
		quantity: commodity.quantity,
		weight: converted.weight ?? 0,
		dimensionLength: converted.length ?? 0,
		dimensionWidth: converted.width ?? 0,
		dimensionHeight: converted.height ?? 0,
		nmfc: commodity.nmfc ?? "",
		isStackable: commodity.isStackable ?? false,
		isHazmat: commodity.isHazmat ?? false,

		// Convert complex objects to HandlingUnit format
		unit: {
			id: commodity.unitTypeId?.toString() ?? "0",
			name: commodity.unitType?.name ?? "",
			code: commodity.unitType?.name ?? "", // Assuming code matches name, adjust if needed
		},
		class: commodity.freightClass
			? {
					id: commodity.freightClassId?.toString() ?? "0",
					name: commodity.freightClass.name,
					code: commodity.freightClass.code,
				}
			: {
					id: "0",
					name: "select",
					code: "",
				},

		// Set calculated fields
		density: 0, // Will be calculated below
		length: converted.length ?? 0,
		width: converted.width ?? 0,
		height: converted.height ?? 0,

		// Set default values for HandlingUnit-specific fields
		name: "", // HandlingUnit has name, Commodity doesn't
		estClass: 0, // This would need to be calculated or fetched
		pieceCount: null,
	};

	hu.density = calculateItemDensity(hu, measuringType);

	return hu;
};

const extractId = (
	value: HandlingUnit["unit"] | HandlingUnit["class"],
): number | null => {
	if (value && typeof value === "object") {
		return typeof value.id === "string"
			? Number.parseInt(value.id, 10) || null
			: value.id || null;
	}
	if (typeof value === "string" || typeof value === "number") {
		return Number.parseInt(String(value), 10) || null;
	}
	return null;
};

export const convertHandlingUnitToCommodityNewBody = (
	handlingUnit: HandlingUnit,
	customerId: number,
	updatedByUserId: number,
	measuringType?: string,
): CommodityNewBody => {
	// Get dimensions, preferring length/width/height over dimensionLength/Width/Height
	const length =
		(handlingUnit.length ?? 0) > 0
			? handlingUnit.length
			: handlingUnit.dimensionLength;
	const width =
		(handlingUnit.width ?? 0) > 0
			? handlingUnit.width
			: handlingUnit.dimensionWidth;
	const height =
		(handlingUnit.height ?? 0) > 0
			? handlingUnit.height
			: handlingUnit.dimensionHeight;

	// Convert to imperial if needed and round to integers
	const isMetric = measuringType === "KG/CM";
	const converted = isMetric
		? imperialMetricConverter("LB/IN", {
				weight: handlingUnit.weight,
				length: length,
				width: width,
				height: height,
			})
		: {
				weight: handlingUnit.weight,
				length: length,
				width: width,
				height: height,
			};

	return {
		customerId,
		description: handlingUnit.description ?? null,
		unitTypeId: extractId(handlingUnit.unit) ?? 0,
		quantity: handlingUnit.quantity ?? null,
		// eslint-disable-next-line unicorn/no-negated-condition -- Need to check for null explicitly to allow 0 values
		weight: converted.weight != null ? Math.round(converted.weight) : null,

		dimensionLength:
			converted.length == null ? null : Math.round(converted.length),

		dimensionWidth:
			converted.width == null ? null : Math.round(converted.width),

		dimensionHeight:
			converted.height == null ? null : Math.round(converted.height),
		freightClassId: extractId(handlingUnit.class),
		nmfc: handlingUnit.nmfc ?? null,
		isStackable: handlingUnit.isStackable ?? null,
		isHazmat: handlingUnit.isHazmat ?? null,
		updatedByUserId,
	};
};
